import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core';

import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig, GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { hasTarget } from '@frontend/sports/common/core/utils/dom';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { GridEvent, GridMedia, Group } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { flatten, keys } from 'lodash-es';

import { BetBuilderConfigService } from '../../betbuilder/services/betbuilder-config.service';
import { isBetbuilderEvent } from '../../betbuilder/utils';
import { EpcotConfigService, EpcotModule } from '../../common/epcot-config.service';
import { OptionGroupService } from '../../event-model/helpers/option-group.service';
import { RedirectHelperService } from '../../navigation-core/redirect-helper.service';
import { EventLinkConfig, UrlHelperService } from '../../navigation-core/url-helper.service';
import { StatisticsConfigService } from '../../statistics/statistics-config.service';
import { GameNotesService } from '../game-notes.service';

@Component({
    selector: 'ms-six-pack-event',
    templateUrl: 'six-pack-event.component.html',
    styleUrls: ['./six-pack-event.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SixPackEventComponent implements OnInit, OnChanges {
    @Input() event: GridEvent;
    @Input() groups: Group[];
    @Input() tracking: TrackingSource;
    @Input() linkConfig: EventLinkConfig;
    @Input() media: GridMedia;
    @Input() favouriteToggle: boolean;
    @Input() promotedFixtureIdsMap: { [fixtureId: number]: number } = {};
    @Input() optionGroupTemplate: TemplateRef<any>;
    @Input() showParticipantImage: boolean;
    @Input() calendarInfoDisplay = false;
    @Input() showAllWagers: boolean | undefined;
    @Input() betDetails = '';
    @Input() gameNotes?: string;
    @Input() showLogoColor: boolean;
    @HostBinding('class') className = 'grid-event grid-six-pack-event ms-active-highlight';
    @HostBinding('class.two-lined-name') get nameOnTwoLines(): boolean {
        return this.gridConfig.showParticipantLogoOrImage;
    }

    @HostBinding('class.all-wagers-absent')
    get eventFooterAbsentClass(): boolean {
        return !this.isAllMarketsLinkEnabled;
    }

    eventUrl: string;
    shouldRemoveHref: Nullable<boolean>;
    groupsMap: { [key: string]: EventOptionGroup };

    empty: boolean;
    emptyText: string;
    emptyChars = 15;
    decorateAttribute: boolean;
    isEpcotEnabled = false;
    showParticipantStats = false;
    isTwoThreeWayEnabled = false;
    showSuspendedSelection: boolean;
    showGameNotes: boolean;
    isBuildParleyVisible: boolean;
    isAllMarketsLinkEnabled: boolean;
    gridEventDisplayOverride = false;
    constructor(
        public sitecore: Sitecore,
        public gridConfig: GridConfig,
        private urlHelper: UrlHelperService,
        private optionGroupService: OptionGroupService,
        private deviceService: DeviceService,
        private nativeAppService: NativeAppService,
        private redirectHelper: RedirectHelperService,
        private epcotConfigService: EpcotConfigService,
        private gameNotesService: GameNotesService,
        private statisticsConfigService: StatisticsConfigService,
        private eventDetailsConfig: EventDetailsConfig,
        private betBuilderConfigService: BetBuilderConfigService,
    ) {
        this.isAllMarketsLinkEnabled = this.gridConfig.isAllMarketsLinkEnabled;
        this.showSuspendedSelection = this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled;
        this.gridEventDisplayOverride = this.gridConfig.gridEventDisplayVersion === 'V2';
    }

    eventClick(event: Event): void {
        if (!this.deviceService.isTouch && !this.nativeAppService.isTerminal) {
            if (hasTarget(event, HTMLAnchorElement)) {
                return;
            }
            this.redirectHelper.goToPage(this.eventUrl);
        }
    }

    eventInfoClick(): void {
        this.redirectHelper.goToPage(this.eventUrl);
    }

    ngOnInit(): void {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled(EpcotModule.Grid);
        this.isTwoThreeWayEnabled = this.isEpcotEnabled && this.gridConfig.is2way3wayEnabled;
        this.showParticipantStats = this.isEpcotEnabled
            ? this.statisticsConfigService.showStats(this.event.sport.id) && this.gridConfig.is2way3wayEnabled
            : this.statisticsConfigService.showStats(this.event.sport.id) && this.gridConfig.isAllMarketsLinkEnabled;
        this.emptyText = this.sitecore.eventGrid.emptyMarketLinkText;
        const disableBadges = this.promotedFixtureIdsMap[this.event.id as any as number] && this.deviceService.isMobilePhone;
        this.isBuildParleyVisible = !disableBadges && isBetbuilderEvent(this.event, this.betBuilderConfigService);

        this.setOptionGroup();
        this.setLink();
        this.shouldRemoveHref = this.urlHelper.shouldRemoveHref();
        if (this.isTwoThreeWayEnabled && this.event) {
            this.gameNotes = this.gameNotesService.getGameNotes(this.event.id);
        }
        this.showGameNotes = !!this.gameNotes && this.gridConfig.isAllMarketsLinkEnabled;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.groups || changes.event) {
            this.setOptionGroup();
            this.setLink();
        }

        if (changes.linkConfig) {
            this.setLink();
        }
        if (this.showAllWagers !== undefined) {
            this.isAllMarketsLinkEnabled = this.gridConfig.isAllMarketsLinkEnabled && this.showAllWagers;
        }
    }

    trackGroup(index: number, group: Group): string {
        return group.id;
    }

    emptySiblings(group: Group): boolean {
        // we need to make sure that we have two consecutive offline option groups
        const map = this.getEmptyMap(group);

        return map.length === 2 && Math.abs(map[0] - map[1]) === 1;
    }

    emptySixPack(group: Group): boolean {
        const map = this.getEmptyMap(group);

        return map.length === 3;
    }

    private getEmptyMap(group: Group): number[] {
        if (!group || !group.childGroups) {
            return [];
        }

        const map: number[] = [];
        group.childGroups.forEach((id, index) => {
            if (!this.groupsMap[id] || !this.groupsMap[id].online) {
                map.push(index);
            }
        });

        return map;
    }

    private setLink(): void {
        this.eventUrl = this.urlHelper.getEventUrl(this.event, this.linkConfig.extraParams);
        this.emptyText = this.linkConfig.emptyText;
    }

    private setOptionGroup(): void {
        this.groupsMap = {};

        if (!this.showSuspendedSelection && !this.groups) {
            this.empty = true;
        } else {
            flatten(this.groups.map((current) => current.childGroups || [])).forEach((current) => {
                const optionGroup = this.optionGroupService.get(this.event.optionGroups, current);

                if (optionGroup) {
                    this.groupsMap[current] = optionGroup;
                }
            });

            const optionGroups = keys(this.groupsMap || {});
            const optionGroupsFilter = (id: string) => !this.groupsMap[id] || !this.groupsMap[id].online;

            this.empty = !this.showSuspendedSelection && (optionGroups.length === 0 || optionGroups.every(optionGroupsFilter));
            this.decorateAttribute = true;
        }
    }
}
